import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { uuidv4 } from "@-/com.npmjs.uuid";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import { captureAnalyticsEvent, setAnalyticsContext } from "@-/dev.improve.haniverse.lib.analytics";
import { useCallback, useEffect, useRef } from "react";
import {
  createStudySessionClock,
  STUDY_CHECKPOINT_MS,
  type StudySessionCheckpoint,
  type StudySessionEnd,
} from "#study-session-clock";
import { subscribeStudyPresence } from "#subscribe-study-presence";
import { saveStudySession } from "#save-study-session";
import {
  clearOpenStudySessionMarker,
  takeOpenStudySessionMarker,
  writeOpenStudySessionMarker,
} from "#open-study-session-marker";

export function useStudySession(enabled: boolean, activityKey: unknown) {
  const [db] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const { mutate } = useMutation({
    mutationFn: ({
      database,
      session,
    }: {
      database: ActorDatabase;
      session: StudySessionCheckpoint;
    }) => saveStudySession(database, session),
    mutationKey: ["saveStudySession", db],
    retry: 3,
  });
  // A session still marked open at mount was never closed: the process ended mid-session.
  useEffect(() => {
    const leftover = takeOpenStudySessionMarker();
    if (leftover !== null) {
      captureAnalyticsEvent(
        "study_session_ended",
        {
          duration_seconds: studySeconds(
            Date.parse(leftover.startedAt),
            Date.parse(leftover.endedAt),
          ),
          end_reason: "process_ended",
          recovered: true,
        },
        { dedupeKey: leftover.id, timestamp: new Date(leftover.endedAt) },
      );
    }
  }, []);
  const clockRef = useRef<ReturnType<typeof createStudySessionClock> | null>(null);
  useEffect(() => {
    const clock = createStudySessionClock({
      createId: uuidv4,
      onSessionEnd: reportStudySessionEnd,
      onSessionStart: ({ id }) => {
        setAnalyticsContext({ study_session_id: id });
        captureAnalyticsEvent("study_session_started", {});
      },
      save: (session) => {
        writeOpenStudySessionMarker(session);
        mutate({ database: db, session });
      },
    });
    clockRef.current = clock;
    const unsubscribe = subscribeStudyPresence(
      (active) => clock.setEnabled(enabled && active),
      clock.activity,
    );
    const interval = setInterval(clock.checkpoint, STUDY_CHECKPOINT_MS);
    return () => {
      clock.setEnabled(false);
      clockRef.current = null;
      clearInterval(interval);
      unsubscribe();
    };
  }, [db, enabled, mutate]);
  useEffect(() => {
    clockRef.current?.activity();
  }, [activityKey]);
  return useCallback(() => clockRef.current?.activity(), []);
}

function reportStudySessionEnd({ end, id, reason, start }: StudySessionEnd): void {
  clearOpenStudySessionMarker();
  captureAnalyticsEvent(
    "study_session_ended",
    { duration_seconds: studySeconds(start, end), end_reason: reason, recovered: false },
    { dedupeKey: id, timestamp: new Date(end) },
  );
  setAnalyticsContext({ study_session_id: undefined });
}

function studySeconds(start: number, end: number): number {
  return Math.max(0, Math.round((end - start) / 1000));
}
